import Parse from 'parse/dist/parse.min.js';

/**
 * Inicializa los roles de usuario si no existen
 * Esta función debe llamarse al iniciar la aplicación
 */
export const initializeUserRoles = async () => {
  try {
    // Verificar si ya existe el rol de administrador
    const adminRoleQuery = new Parse.Query(Parse.Role);
    adminRoleQuery.equalTo('name', 'Administrator');
    let adminRole = await adminRoleQuery.first({ useMasterKey: true });
    
    // Si no existe, crear el rol de administrador
    if (!adminRole) {
      const acl = new Parse.ACL();
      acl.setPublicReadAccess(true);
      
      adminRole = new Parse.Role('Administrator', acl);
      await adminRole.save(null, { useMasterKey: true });
      console.log('Rol de administrador creado exitosamente');
    }
    
    // Verificar si ya existe el rol de cliente
    const clientRoleQuery = new Parse.Query(Parse.Role);
    clientRoleQuery.equalTo('name', 'Client');
    let clientRole = await clientRoleQuery.first({ useMasterKey: true });
    
    // Si no existe, crear el rol de cliente
    if (!clientRole) {
      const acl = new Parse.ACL();
      acl.setPublicReadAccess(true);
      
      clientRole = new Parse.Role('Client', acl);
      await clientRole.save(null, { useMasterKey: true });
      console.log('Rol de cliente creado exitosamente');
    }
    
    return { success: true, message: 'Roles inicializados correctamente' };
  } catch (error) {
    console.error('Error al inicializar roles:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtiene todos los usuarios registrados en el sistema
 * @returns {Array} - Lista de usuarios
 */
export const getAllUsers = async () => {
  try {
    // Verificar si el usuario actual es administrador
    const currentUser = Parse.User.current();
    const isAdmin = currentUser && (currentUser.get('role') === 'admin' || currentUser.get('isAdmin') === true);
    
    // Obtener el usuario actual si está disponible
    const currentUserData = currentUser ? [{
      id: currentUser.id,
      email: currentUser.get('email'),
      name: currentUser.get('name') || currentUser.get('username'),
      role: currentUser.get('role') || 'client',
      isAdmin: currentUser.get('isAdmin') || false,
      emailVerified: currentUser.get('emailVerified') || false,
      createdAt: currentUser.get('createdAt'),
      lastLogin: currentUser.get('lastLogin'),
      status: currentUser.get('status') || 'active'
    }] : [];
    
    if (!isAdmin) {
      console.warn('Solo los administradores pueden ver todos los usuarios');
      // Si no es admin, solo devolver el usuario actual
      return currentUserData;
    }
    
    // Método para administradores: usar Cloud Function para obtener todos los usuarios
    try {
      // Llamar a la Cloud Function que usa Master Key
      const results = await Parse.Cloud.run('getAllUsers');
      return results.map(user => ({
        id: user.id,
        email: user.email,
        name: user.name || user.username,
        role: user.role || 'client',
        isAdmin: user.isAdmin || false,
        emailVerified: user.emailVerified || false,
        createdAt: user.createdAt,
        lastLogin: user.lastLogin,
        status: user.status || 'active'
      }));
    } catch (queryError) {
      console.warn('Error al obtener usuarios con Cloud Function:', queryError);
      // Si falla la consulta, intentar con método estándar
      try {
        const query = new Parse.Query(Parse.User);
        query.limit(1000);
        query.ascending('createdAt');
        const results = await query.find();
        return results.map(user => ({
          id: user.id,
          email: user.get('email'),
          name: user.get('name') || user.get('username'),
          role: user.get('role') || 'client',
          isAdmin: user.get('isAdmin') || false,
          emailVerified: user.get('emailVerified') || false,
          createdAt: user.get('createdAt'),
          lastLogin: user.get('lastLogin'),
          status: user.get('status') || 'active'
        }));
      } catch (standardError) {
        console.warn('Error al obtener usuarios con query estándar:', standardError);
        // Si todo falla, devolver solo el usuario actual
        return currentUserData;
      }
    }
  } catch (error) {
    console.error('Error general al obtener usuarios:', error);
    throw error;
  }
};

/**
 * Obtiene estadísticas de usuarios
 * @returns {Object} - Estadísticas de usuarios
 */
export const getUserStats = async () => {
  try {
    const allUsers = await getAllUsers();
    
    // Estadísticas básicas
    const totalUsers = allUsers.length;
    const activeUsers = allUsers.filter(user => user.emailVerified).length;
    
    // Usuarios nuevos este mes
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const newThisMonth = allUsers.filter(user => 
      new Date(user.createdAt) >= firstDayOfMonth
    ).length;
    
    // Usuarios por rol
    const usersByRole = {
      admin: allUsers.filter(user => user.role === 'admin' || user.isAdmin).length,
      client: allUsers.filter(user => user.role === 'client' && !user.isAdmin).length
    };
    
    return {
      total: totalUsers,
      active: activeUsers,
      newThisMonth,
      usersByRole
    };
  } catch (error) {
    console.error('Error al obtener estadísticas de usuarios:', error);
    throw error;
  }
};

/**
 * Obtiene un usuario por su ID
 * @param {string} userId - ID del usuario
 * @returns {Object} - Datos del usuario
 */
export const getUserById = async (userId) => {
  try {
    // Verificar si el usuario actual es administrador
    const currentUser = Parse.User.current();
    const isAdmin = currentUser && (currentUser.get('role') === 'admin' || currentUser.get('isAdmin') === true);
    
    // Si el usuario actual es el mismo que se está consultando, permitir acceso
    const isSelfQuery = currentUser && currentUser.id === userId;
    
    if (!isAdmin && !isSelfQuery) {
      console.warn('Acceso denegado: Solo puedes ver tu propio perfil o necesitas ser administrador');
      return null;
    }
    
    try {
      // Intentar primero con Cloud Function (si es admin)
      if (isAdmin && !isSelfQuery) {
        const result = await Parse.Cloud.run('getUserById', { userId });
        if (result) {
          return {
            id: result.id,
            email: result.email,
            name: result.name || result.username,
            role: result.role || 'client',
            isAdmin: result.isAdmin || false,
            emailVerified: result.emailVerified || false,
            createdAt: result.createdAt,
            lastLogin: result.lastLogin,
            status: result.status || 'active'
          };
        }
      }
      
      // Método estándar como fallback
      const query = new Parse.Query(Parse.User);
      const user = await query.get(userId);
      
      if (!user) return null;
      
      return {
        id: user.id,
        email: user.get('email'),
        name: user.get('name') || user.get('username'),
        role: user.get('role') || 'client',
        isAdmin: user.get('isAdmin') || false,
        emailVerified: user.get('emailVerified') || false,
        createdAt: user.get('createdAt'),
        lastLogin: user.get('lastLogin'),
        status: user.get('status') || 'active'
      };
    } catch (error) {
      console.error(`Error al obtener usuario con ID ${userId}:`, error);
      return null;
    }
  } catch (error) {
    console.error(`Error general al obtener usuario con ID ${userId}:`, error);
    return null;
  }
};

/**
 * Asigna un rol a un usuario
 * @param {string} userId - ID del usuario
 * @param {string} roleName - Nombre del rol ('Administrator' o 'Client')
 * @returns {Object} - Resultado de la operación
 */
export const assignRoleToUser = async (userId, roleName) => {
  try {
    // Verificar si el usuario actual es administrador
    const currentUser = Parse.User.current();
    const isAdmin = currentUser && (currentUser.get('role') === 'admin' || currentUser.get('isAdmin') === true);
    
    if (!isAdmin) {
      console.warn('Solo los administradores pueden asignar roles');
      return { success: false, error: 'Acceso denegado' };
    }
    
    // Obtener el rol
    const roleQuery = new Parse.Query(Parse.Role);
    roleQuery.equalTo('name', roleName);
    const role = await roleQuery.first();
    
    if (!role) {
      return { success: false, error: `El rol ${roleName} no existe` };
    }
    
    // Obtener el usuario
    const userQuery = new Parse.Query(Parse.User);
    const user = await userQuery.get(userId);
    
    if (!user) {
      return { success: false, error: 'Usuario no encontrado' };
    }
    
    // Asignar el rol al usuario mediante Cloud Function
    try {
      const result = await Parse.Cloud.run('assignRoleToUser', { userId, roleName });
      return result;
    } catch (cloudError) {
      console.error('Error al asignar rol mediante Cloud Function:', cloudError);
      
      // Método alternativo: actualizar campo role en el usuario
      try {
        user.set('role', roleName === 'Administrator' ? 'admin' : 'client');
        user.set('isAdmin', roleName === 'Administrator');
        await user.save(null, { useMasterKey: true });
        return { success: true, message: `Rol ${roleName} asignado correctamente` };
      } catch (saveError) {
        console.error('Error al guardar rol en usuario:', saveError);
        return { success: false, error: saveError.message };
      }
    }
  } catch (error) {
    console.error('Error al asignar rol:', error);
    return { success: false, error: error.message };
  }
};
